import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import maerskLogo from "../images/maersk-logo.png";

const DocIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4fc3d8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="12" y2="17"/>
  </svg>
);
const RulesIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4fc3d8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
  </svg>
);
const DashIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4fc3d8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
  </svg>
);
const ShieldIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#4fc3d8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const DbIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#4fc3d8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="5" rx="9" ry="3"/>
    <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/>
    <path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3"/>
  </svg>
);
const StarIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="#fff" stroke="none">
    <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
  </svg>
);

const CARDS = [
  { title: "Proof of Payment", Icon: DocIcon,   desc: "Upload and extract PoP metadata using AI." },
  { title: "Rule Engine",      Icon: RulesIcon, desc: "Automated compliance checks against GCSS & S4." },
  { title: "Dashboard",        Icon: DashIcon,  desc: "Track cases and monitor validation results." },
];

export default function LoginPage() {
  const { instance } = useMsal();
  const handleLogin = () => instance.loginRedirect(loginRequest);

  return (
    <>
      <style>{`html,body,#root{margin:0;padding:0;height:100%;}*{box-sizing:border-box;}`}</style>

      <div style={{
        height: "100vh",
        background: "#071e32",
        fontFamily: '"Maersk Text", system-ui, -apple-system, "Segoe UI", sans-serif',
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 52px",
        overflow: "hidden",
      }}>
        <div style={{
          display: "flex",
          alignItems: "stretch",
          gap: "52px",
          width: "100%",
          maxWidth: "1300px",
        }}>

          {/* ── Left content ── */}
          <div style={{ flex: 1, minWidth: 0 }}>

            {/* Logo + platform label — sits just above the heading */}
            <div style={{ marginBottom: "16px", marginTop: "60px" }}>
              <img src={maerskLogo} alt="Maersk" style={{ height: "30px", display: "block", marginBottom: "10px" }} />
              <p style={{
                fontSize: "0.78rem",
                fontWeight: 700,
                letterSpacing: "0.14em",
                color: "#4fc3d8",
                textTransform: "uppercase",
                margin: 0,
              }}>
                Maersk AFR Platform
              </p>
            </div>

            {/* AFR + Digital Assistant on one line */}
            <h1 style={{
              fontSize: "5.2rem",
              fontWeight: 800,
              color: "#fff",
              lineHeight: 1,
              marginBottom: "14px",
              letterSpacing: "-0.02em",
              whiteSpace: "nowrap",
            }}>
              AFR{" "}
              <span style={{ fontSize: "2.6rem", fontWeight: 500, letterSpacing: "-0.01em" }}>
                Digital Assistant
              </span>
            </h1>

            {/* Description — single line */}
            <p style={{
              fontSize: "1rem",
              color: "#8baabb",
              marginBottom: "20px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}>
              AI-powered proof-of-payment validation and compliance checks for Approve Freight Release workflows.
            </p>

            {/* Pills */}
            <div style={{ display: "flex", gap: "8px", marginBottom: "22px" }}>
              {["AI-powered extraction", "Automated rule validation", "Secure SSO access"].map(label => (
                <span key={label} style={{
                  padding: "6px 16px",
                  border: "1px solid #1e4060",
                  borderRadius: "999px",
                  fontSize: "0.88rem",
                  fontWeight: 500,
                  color: "#c8dde8",
                  whiteSpace: "nowrap",
                }}>
                  {label}
                </span>
              ))}
            </div>

            {/* Cards */}
            <div style={{ display: "flex", gap: "14px" }}>
              {CARDS.map(({ title, Icon, desc }) => (
                <div key={title} style={{
                  flex: 1,
                  background: "#0d2840",
                  borderRadius: "12px",
                  padding: "20px 18px",
                  border: "1px solid #1a3d58",
                }}>
                  <div style={{
                    width: "42px", height: "42px",
                    background: "#0f3650",
                    borderRadius: "10px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    marginBottom: "12px",
                  }}>
                    <Icon />
                  </div>
                  <h3 style={{ fontSize: "1.05rem", fontWeight: 700, color: "#fff", marginBottom: "6px" }}>
                    {title}
                  </h3>
                  <p style={{ fontSize: "0.87rem", color: "#7a9db5", lineHeight: 1.5, margin: 0 }}>{desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right sign-in card ── */}
          <div style={{
            width: "440px",
            flexShrink: 0,
            background: "#c8d8e4",
            borderRadius: "16px",
            padding: "40px 36px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
              <div style={{
                background: "#00243d",
                borderRadius: "10px",
                padding: "8px 14px",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <img src={maerskLogo} alt="Maersk" style={{ height: "26px", display: "block" }} />
              </div>
              <span style={{ fontSize: "1.05rem", fontWeight: 700, color: "#071e32", letterSpacing: "0.06em" }}>
                AFR ASSISTANT
              </span>
            </div>

            <h2 style={{ fontSize: "1.6rem", fontWeight: 700, color: "#071e32", marginBottom: "8px" }}>
              Sign in to AFR Assistant
            </h2>
            <p style={{ fontSize: "0.92rem", color: "#3a5568", marginBottom: "24px", lineHeight: 1.5 }}>
              Use your Maersk account to access AFR validation securely.
            </p>

            <div style={{ marginBottom: "26px" }}>
              {[
                { Icon: ShieldIcon, text: "Enterprise Security — Maersk Entra ID SSO" },
                { Icon: DbIcon,     text: "Unified Data Access — Live GCSS & S4 data" },
              ].map(({ Icon, text }) => (
                <div key={text} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                  <Icon />
                  <span style={{ fontSize: "0.9rem", color: "#1e3a4f", fontWeight: 500 }}>{text}</span>
                </div>
              ))}
            </div>

            <button
              onClick={handleLogin}
              style={{
                width: "100%",
                padding: "15px",
                background: "#071e32",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                fontSize: "1rem",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                marginBottom: "16px",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
                <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
              </svg>
              Continue with Microsoft SSO
            </button>

            <p style={{ fontSize: "0.75rem", color: "#4a6a80", textAlign: "center", margin: 0 }}>
              Maersk employees only · Powered by Entra ID
            </p>
          </div>

        </div>
      </div>
    </>
  );
}
